/* eslint @typescript-eslint/no-explicit-any: 0  */
import * as React from 'react';
import flattenChildren from 'react-flatten-children';
import { classes } from './strings';

type Props = React.Attributes & Partial<any>;

const setProps = (
    node: React.ReactNode | JSX.Element,
    props: Props | ((current: Props) => Props)
): React.ReactNode => {
    if (React.isValidElement(node)) {
        const newProps = props instanceof Function ? props(node.props) : props;
        return React.cloneElement(node, newProps);
    }
    return node;
};

const appendProps = (
    node: React.ReactNode | JSX.Element,
    props: Props
): React.ReactNode => {
    if (React.isValidElement(node)) {
        return React.cloneElement(node, { ...node.props, ...props });
    }
    return node;
};

const mergeClassnames = (
    node: React.ReactNode | JSX.Element,
    className: string
): React.ReactNode => {
    if (React.isValidElement(node)) {
        const props = {
            ...node.props,
            className: classes(node.props.className, className),
        };
        return React.cloneElement(node, { ...props });
    }
    return node;
};

const setChildrenProps = (
    children: React.ReactNode,
    props: React.Attributes & Partial<any>
): React.ReactNode => {
    return React.Children.map(flattenChildren(children), (child, key) => {
        return setProps(child, { key, ...props });
    });
};

const appendChildrenProps = (
    children: React.ReactNode,
    props: React.Attributes & Partial<any>
): React.ReactNode => {
    return React.Children.map(flattenChildren(children), (child, key) => {
        return appendProps(child, { key, ...props });
    });
};

const mergeChildrenClassnames = (
    children: React.ReactNode,
    className: string
): React.ReactNode => {
    return React.Children.map(flattenChildren(children), child => {
        return mergeClassnames(child, className);
    });
};

const getValidChildren = (children: React.ReactNode): React.ReactNode[] =>
    React.Children.toArray(children).filter(child =>
        React.isValidElement(child)
    );

const hasValidChildren = (children: React.ReactNode): boolean =>
    getValidChildren(children).length > 0;

export {
    getValidChildren,
    hasValidChildren,
    setProps,
    setChildrenProps,
    appendProps,
    appendChildrenProps,
    mergeChildrenClassnames,
    mergeClassnames,
};
